/**
 * AuthedFetch — shared auth-failure gate for dashboard data-layer fetches
 * (message tracking, campaign analytics).
 *
 * Mirrors the proven gift-registry.js apiCall/authInvalid pattern: once a
 * request 401s (or the Firebase session is gone), every subsequent call
 * fails fast WITHOUT touching the network, and pollers self-terminate —
 * instead of an open dashboard tab hammering an auth-gated endpoint
 * tokenless every 30s after the session dies (the incident this was built
 * for: message-tracking 401-floods post-deploy).
 *
 * Contract: .fetch() always resolves to a Response (real, or a synthetic
 * 401), so existing callers' .ok / .json() / .catch handling is unchanged.
 *
 * Self-contained: it manages its own onAuthStateChanged subscription, so no
 * other file needs to know about it beyond calling window.AuthedFetch.fetch
 * and (optionally) checking window.AuthedFetch.authInvalid to stop a timer.
 */
(function () {
  'use strict';

  let authInvalid = false;
  let promptShown = false;

  function resolveAuth() {
    try {
      if (typeof firebase !== 'undefined' && firebase.auth) return firebase.auth();
    } catch (e) {}
    return window.firebaseAuth || null;
  }

  // Firebase restores the persisted session asynchronously — currentUser is
  // null for ~0.5–2s on first load. Fast path if it's already there; else
  // wait for the first definitive auth state, with a bounded fallback so we
  // never hang.
  function currentUser() {
    const auth = resolveAuth();
    if (!auth) return Promise.resolve(null);
    if (auth.currentUser) return Promise.resolve(auth.currentUser);
    return new Promise(function (resolve) {
      let settled = false;
      let unsub = null;
      const done = function (u) {
        if (settled) return;
        settled = true;
        try { if (unsub) unsub(); } catch (e) {}
        resolve(u || null);
      };
      try {
        unsub = auth.onAuthStateChanged(function (u) { done(u); }, function () { done(null); });
      } catch (e) { done(null); return; }
      setTimeout(function () { done(auth.currentUser || null); }, 4000);
    });
  }

  function syntheticUnauthorized() {
    return new Response(JSON.stringify({ error: 'auth_invalid' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function syntheticUnavailable() {
    return new Response(JSON.stringify({ error: 'network' }), {
      status: 503,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Non-intrusive, once-only. Prefers the app's canonical re-auth entry
  // point; never auto-reloads (could interrupt an in-progress action).
  function surfaceReauth() {
    if (promptShown) return;
    promptShown = true;
    try {
      if (window.authManager && typeof window.authManager.showAuthModal === 'function') {
        window.authManager.showAuthModal('your dashboard');
        return;
      }
    } catch (e) {}
    try {
      if (document.getElementById('authedFetchReauthBanner')) return;
      const bar = document.createElement('div');
      bar.id = 'authedFetchReauthBanner';
      bar.setAttribute('role', 'status');
      bar.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;' +
        'background:#664d03;color:#fff;padding:10px 16px;font-size:14px;' +
        'display:flex;align-items:center;justify-content:center;gap:12px;' +
        'box-shadow:0 -2px 8px rgba(0,0,0,.2)';
      const msg = document.createElement('span');
      msg.textContent = 'Your session expired — some data stopped updating.';
      const refresh = document.createElement('button');
      refresh.textContent = 'Refresh';
      refresh.style.cssText = 'background:#fff;color:#664d03;border:0;border-radius:4px;' +
        'padding:5px 14px;font-weight:600;cursor:pointer';
      refresh.addEventListener('click', function () { window.location.reload(); });
      const dismiss = document.createElement('button');
      dismiss.textContent = 'Dismiss';
      dismiss.setAttribute('aria-label', 'Dismiss');
      dismiss.style.cssText = 'background:transparent;color:#fff;border:1px solid rgba(255,255,255,.6);' +
        'border-radius:4px;padding:5px 12px;cursor:pointer';
      dismiss.addEventListener('click', function () { bar.remove(); });
      bar.appendChild(msg);
      bar.appendChild(refresh);
      bar.appendChild(dismiss);
      (document.body || document.documentElement).appendChild(bar);
    } catch (e) {}
  }

  function markValid() {
    authInvalid = false;
    promptShown = false;
  }

  // surface=true only when triggered by an actual failed request — NOT from
  // a normal signed-out load (the page's own auth gate handles that, and we
  // don't want to prompt during a redirect).
  function markInvalid(surface) {
    const firstTrip = !authInvalid;
    authInvalid = true;
    if (surface && firstTrip) surfaceReauth();
  }

  const AuthedFetch = {
    get authInvalid() { return authInvalid; },
    markValid: markValid,
    markInvalid: function () { markInvalid(true); },

    /**
     * Just the Authorization header, for callers that keep their own fetch
     * (a write with its own error handling, say) but still need the token.
     *
     * Use this instead of `await firebase.auth().currentUser?.getIdToken()`.
     * That expression awaits `undefined` while the session is still
     * restoring — the first ~0.5-2s of every page load — so it yields
     * `undefined` and the caller sends the literal string "Bearer undefined".
     * The server sees a token, skips any tokenless read-through, and answers
     * 401. That is what emptied the demo Seating tab for every signed-out
     * visitor (fixed 2026-09-11) and what still refused a host's save if they
     * were quick off the mark.
     *
     * Returns {} when genuinely signed out, so spreading it simply omits the
     * header rather than sending a broken one. Unlike .fetch() it does not
     * trip the fail-fast gate: the caller owns that request's outcome.
     */
    authHeaders: function () {
      return currentUser().then(function (user) {
        if (!user) return {};
        return user.getIdToken().then(function (token) {
          return { Authorization: 'Bearer ' + token };
        }).catch(function () { return {}; });
      }).catch(function () { return {}; });
    },

    /**
     * @param {string} path
     * @param {object} [init] fetch init (headers merged; Authorization added)
     * @returns {Promise<Response>} real Response, or synthetic 401
     */
    fetch: function (path, init) {
      if (authInvalid) {
        return Promise.resolve(syntheticUnauthorized());
      }
      return currentUser().then(function (user) {
        if (!user) {
          markInvalid(true);
          return syntheticUnauthorized();
        }
        const send = function (forceRefresh) {
          return user.getIdToken(forceRefresh).then(function (token) {
            const opts = Object.assign({}, init);
            opts.headers = Object.assign({}, init && init.headers, {
              Authorization: 'Bearer ' + token
            });
            return fetch(path, opts);
          });
        };
        // A 401 is often just a stale token (a laptop waking from sleep):
        // try once more with a fresh one before calling the session dead.
        return send(false).then(function (res) {
          if (res.status !== 401) return res;
          return send(true).then(function (retry) {
            if (retry.status === 401) markInvalid(true);
            return retry;
          });
        });
      }).catch(function () {
        // A network failure (phone offline, request dropped), not a lost
        // session: telling the host to sign in again was wrong, and tripping
        // the gate froze every poller for the rest of the visit. Fail just
        // this request; the next poll tries again.
        return syntheticUnavailable();
      });
    }
  };

  window.AuthedFetch = AuthedFetch;

  // Own the auth lifecycle: reset the gate on (re-)auth, trip it on sign-out
  // (without surfacing — the page's own auth gate handles signed-out and may
  // redirect; we just stop the pollers hammering). Retry hooking until the
  // Firebase SDK is ready.
  (function hookAuth() {
    const auth = resolveAuth();
    if (!auth || typeof auth.onAuthStateChanged !== 'function') {
      setTimeout(hookAuth, 500);
      return;
    }
    auth.onAuthStateChanged(function (user) {
      if (user) markValid();
      else markInvalid(false);
    });
  })();
})();
